import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

export const UserPasswordModal = ({ show, onHide, user }) => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [newPass,   setNewPass]   = useState("");
  const [showPass,  setShowPass]  = useState(false);
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await http.get("user_password", { params: { user_id: user.id } });
      if (res.data?.status) setData(res.data);
      else toast.error(res.data?.msg || "Failed to load");
    } catch { toast.error("Server error"); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (show) { setNewPass(""); setShowPass(false); load(); }
  }, [show, load]);

  const handleReset = async () => {
    if (!newPass.trim()) { toast.warn("Enter a new password"); return; }
    if (newPass.length < 4) { toast.warn("Password must be at least 4 characters"); return; }
    setSaving(true);
    try {
      const res = await http.put("user_password", { user_id: user.id, new_password: newPass });
      if (res.data?.status) {
        toast.success("Password updated successfully");
        setNewPass("");
        load();
      } else {
        toast.error(res.data?.msg || "Update failed");
      }
    } catch { toast.error("Server error"); }
    finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="sm">
      <div style={ms.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={ms.iconWrap}>
            <Icon icon="solar:lock-password-bold-duotone" style={{ fontSize: 20 }} />
          </div>
          <div>
            <div style={ms.title}>Password Management</div>
            <div style={ms.sub}>{user?.name || "—"}</div>
          </div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 16 }} />
        </button>
      </div>

      <div style={ms.body}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8" }}>
            <span className="spinner-border spinner-border-sm" />
          </div>
        ) : (
          <>
            {/* User info */}
            <div style={ms.infoCard}>
              <div style={ms.infoRow}>
                <span style={ms.infoLabel}>Name</span>
                <span style={ms.infoValue}>{data?.user_info?.name || "—"}</span>
              </div>
              <div style={ms.infoRow}>
                <span style={ms.infoLabel}>Mobile</span>
                <span style={ms.infoValue}>{data?.user_info?.mobile || "—"}</span>
              </div>
              <div style={ms.infoRow}>
                <span style={ms.infoLabel}>UID</span>
                <span style={{ ...ms.infoValue, fontFamily: "monospace", color: "#7c3aed" }}>{data?.user_info?.uid || "—"}</span>
              </div>
              <div style={ms.infoRow}>
                <span style={ms.infoLabel}>Member Since</span>
                <span style={ms.infoValue}>{data?.user_info?.created_at || "—"}</span>
              </div>
            </div>

            {/* Current hash */}
            <div style={ms.hashCard}>
              <div style={ms.hashLabel}>
                <Icon icon="solar:key-bold-duotone" style={{ fontSize: 14, marginRight: 5, color: "#f59e0b" }} />
                Password Hash (MD5)
              </div>
              <div style={ms.hashValue}>{data?.password_hash || "—"}</div>
              <div style={ms.hashNote}>Passwords are stored as MD5 hashes. You can set a new plain-text password below.</div>
            </div>

            {/* Reset */}
            <div style={{ marginTop: 16 }}>
              <label style={ms.label}>Set New Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPass ? "text" : "password"}
                  style={ms.input}
                  placeholder="Min 4 characters"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleReset()}
                />
                <button style={ms.eyeBtn} onClick={() => setShowPass(!showPass)} type="button">
                  <Icon icon={showPass ? "solar:eye-closed-bold" : "solar:eye-bold"} style={{ fontSize: 16 }} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide}>Cancel</button>
        <button style={ms.saveBtn} onClick={handleReset} disabled={saving || !newPass.trim()}>
          {saving
            ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
            : <Icon icon="solar:lock-password-bold" style={{ fontSize: 15 }} />
          }
          Update Password
        </button>
      </div>
    </Modal>
  );
};

const ms = {
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px", borderBottom: "1px solid #f1f5f9" },
  iconWrap: { width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg,#7c3aed,#a855f7)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  title: { fontWeight: 800, fontSize: "0.95rem", color: "#0f172a" },
  sub:   { fontSize: "0.75rem", color: "#94a3b8" },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 7, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body: { padding: "16px 20px" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px 16px", borderTop: "1px solid #f1f5f9" },

  infoCard: { background: "#f8fafc", borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: "1px solid #e8edf2" },
  infoRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid #f1f5f9" },
  infoLabel: { fontSize: "0.73rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" },
  infoValue: { fontSize: "0.82rem", color: "#1e293b", fontWeight: 600 },

  hashCard: { background: "#fffbeb", borderRadius: 10, padding: "12px 14px", border: "1px solid #fde68a" },
  hashLabel: { fontSize: "0.72rem", fontWeight: 700, color: "#92400e", marginBottom: 6, display: "flex", alignItems: "center" },
  hashValue: { fontFamily: "monospace", fontSize: "0.72rem", color: "#1e293b", wordBreak: "break-all", letterSpacing: "0.02em" },
  hashNote: { fontSize: "0.68rem", color: "#92400e", marginTop: 6 },

  label: { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "9px 40px 9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  eyeBtn: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", alignItems: "center" },

  cancelBtn: { padding: "7px 16px", background: "#f1f5f9", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 600, color: "#475569", cursor: "pointer" },
  saveBtn: { display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", background: "linear-gradient(135deg,#7c3aed,#a855f7)", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, color: "#fff", cursor: "pointer" },
};
